import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  MenuItem,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { GenericDialoagProps } from "Interfaces";
import { useCallback, useEffect, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { ApiComponentBlockCategory } from "@incendium/api";

interface IComponentFormDialogProps
  extends GenericDialoagProps,
  React.HTMLAttributes<HTMLDivElement> {
  onSaved: (name: string, description: string, category?: ApiComponentBlockCategory) => void;
  name?: string;
  description?: string;
  category?: ApiComponentBlockCategory;
  footer?: React.ReactNode;
}

const CATEGORY_OPTIONS = [
  { value: ApiComponentBlockCategory.STRUCTURAL_LAYOUTS, label: "Structural Layouts" },
  { value: ApiComponentBlockCategory.HERO_SECTIONS, label: "Hero sections" },
  { value: ApiComponentBlockCategory.PRODUCT_PURCHASE_BLOCK, label: "Product Purchase Block" },
  { value: ApiComponentBlockCategory.PRODUCT_MEDIA, label: "Product Media" },
  { value: ApiComponentBlockCategory.FEATURES_AND_BENEFITS, label: "Features & Benefits" },
  { value: ApiComponentBlockCategory.COMPARISONS, label: "Comparisons" },
  { value: ApiComponentBlockCategory.CTAS, label: "CTAs" },
  { value: ApiComponentBlockCategory.FAQS, label: "FAQs" },
];

function ComponentFormDialog({
  open,
  setOpen,

  onSaved,
  name: originalName,
  description: originalDescription,
  category: originalCategory,
  children,
  footer,
}: IComponentFormDialogProps) {
  const [name, setName] = useState(originalName || "");
  const [description, setDescription] = useState(originalDescription || "");
  const [category, setCategory] = useState<ApiComponentBlockCategory | undefined>(originalCategory);

  const save = useCallback(() => {
    onSaved(name, description, category);
    setOpen(false);
  }, [name, description, category, onSaved, setOpen]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    setName(originalName || "");
    setDescription(originalDescription || "");
    setCategory(originalCategory);
  }, [originalName, originalDescription, originalCategory, open]);

  return (
    <CustomDialog open={open} fullWidth maxWidth="sm">
      <ValidatorForm onSubmit={save}>
        <DialogTitle>Component Settings</DialogTitle>
        {children && <DialogContent>{children}</DialogContent>}

        <DialogContent>
          <Stack spacing={2}>
            <Box >
              <TextValidator
                fullWidth
                label="name*"
                name="name"
                size="small"
                value={name}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Name is required"]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setName(e.target.value)
                }
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                select
                label="Category"
                name="category"
                value={category || ""}
                variant="outlined"
                size="small"
                onChange={(e) => setCategory(e.target.value as ApiComponentBlockCategory || undefined)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box >
              <TextField
                fullWidth
                multiline
                label="description"
                name="description"
                value={description}
                variant="outlined"
                minRows={2}
                maxRows={6}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDescription(e.target.value)
                }
              />
            </Box>
          </Stack>

        </DialogContent>
        <DialogActions>
          {footer ? (
            <Stack
              direction={"row"}
              spacing={1}
              sx={{ width: "100%" }}
              justifyContent={"space-between"}
            >
              {footer}
              <Stack direction={"row"} spacing={1}>
                <Button onClick={onClose} color="secondary">
                  Cancel
                </Button>
                <Button variant="contained" color="primary" type="submit">
                  Save
                </Button>
              </Stack>
            </Stack>
          ) : (
            <>
              <Button onClick={onClose} color="secondary">
                Cancel
              </Button>
              <Button variant="contained" color="primary" type="submit">
                Save
              </Button>
            </>
          )}
        </DialogActions>
      </ValidatorForm>
    </CustomDialog>
  );
}

export default ComponentFormDialog;

